<template>
  <sortable-list
    :items="effects"
    :order="effects.map((effect) => effect.id)"
    :drag-threshold="20"
    line-class="effect-item__background"
    @update:order="updateOrder($event)"
  >
    <template #default="{ item: effect, index }">
      <div
        class="effect-item"
        :class="{
          '-selected': selectedEffect?.id == effect.id,
          '-focussed': focussedEffect?.id == effect.id
        }"
        @contextmenu.prevent="onEffectContextMenu($event as PointerEvent, effect)"
        @click="onEffectClick($event, effect)"
      >
        <div class="effect-item__header">
          <button
            :ref="(el) => (focusButtonRefs[effect.id] = el as HTMLElement)"
            class="effect-item__title"
            type="button"
            @focus="focussedEffect = effect"
            @blur="focussedEffect = null"
          >
            <span class="effect-item__titletext">
              <template v-if="effect.affectedType === 'target'">{{
                targets[effect.affectedId].title
              }}</template>
              <template v-if="effect.affectedType === 'resource'">{{
                resources[effect.affectedId].title
              }}</template>
            </span>
          </button>
          <documentation-anchor :object="effect" prop="main" class="effect-item__docanchor ml-auto" />
          <button
            :title="$t('actions.openContextMenu')"
            class="button-icon button-icon_small effect-item__contextbutton"
            type="button"
            @click="onEffectContextMenu($event as PointerEvent, effect)"
          >
            <lucide-icon name="more-horizontal" />
          </button>
        </div>

        <div class="effectlist__buttons">
          <expression-input
            v-if="effectRoundLengths[index] <= 1"
            class="effectlist__input"
            :ref="(el: unknown) => effectExpressionFields[effect.id] = (el as ExpressionInputElement)"
            :expression="effect.impulseExpressions?.[0]"
            :parse-context="{
              targets,
              resources,
              indices,
              affectedId: effect.affectedId,
              affectedType: effect.affectedType
            }"
            default-sign="+"
            @update:expression="updateImpulseExpression(effect, 0, $event)"
            @click="onEffectClick($event, null)"
          />
          <div
            v-else
            class="effectlist__summary mr-auto"
            :class="{ '-selected': selectedEffect?.id == effect.id }"
          >
            <i18n-t
              scope="global"
              keypath="labels.effectInXRounds"
              :plural="effectRoundLengths[index]"
            >
              <template #effect>
                <strong>{{ getTotalEffectValue(effect) }}</strong>
              </template>
              <template #n>
                <em>{{ effectRoundLengths[index] }}</em>
              </template>
              <template #roundName>
                {{ $t(`roundUnits.${roundUnit}`, effectRoundLengths[index]) }}
              </template>
            </i18n-t>
          </div>
        </div>
      </div>
    </template>

    <template #after-list>
      <slot></slot>
    </template>
  </sortable-list>

  <context-menu
    :visible="contextMenuData.effect != null"
    :x="contextMenuData.position.x"
    :y="contextMenuData.position.y"
    @close="contextMenuData.effect = null"
  >
    <button
      class="context-menu__entry"
      type="button"
      @click="toClipboard(contextMenuData.effect)"
    >
      {{ $t("actions.copyEffect") }}
    </button>
    <template v-if="selectedEffect?.id !== contextMenuData.effect?.id">
      <button
        class="context-menu__entry"
        type="button"
        @click="onContextmenuOpenRounds(contextMenuData.effect)"
      >
        {{ $t("actions.setEffectRounds", [$t(`roundUnits.${roundUnit}`, 10)]) }}
      </button>
      <button
        class="context-menu__entry"
        type="button"
        @click="onContextMenuOpenVisibility(contextMenuData.effect)"
      >
        {{ $t("labels.effectVisibility") }}
      </button>
    </template>
    <button
      v-else
      class="context-menu__entry"
      type="button"
      @click="onContextmenuDeselect()"
    >
      {{ $t("actions.closeContextmenu") }}
    </button>
    <button
      class="context-menu__entry context-menu__entry--danger"
      type="button"
      @click="onContextmenuDelete(contextMenuData.effect)"
    >
      {{ $t("actions.deleteEffect") }}
    </button>
  </context-menu>
</template>

<script setup lang="ts">
import { computed, ref, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { formatNumber } from "@/helpers/utility/Formatting";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import SortableList from "@core/ui/SortableList.vue";

import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";
import ExpressionInput from "@editor/items/expression/ExpressionInput.vue";
import Effect from "@editor/items/effect/Effect.js";

import { calculateExpression } from "../expression/expressionFunctions";

import { useEffectStore } from "@editor/items/effect/effects.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";

import type { EffectItem } from "./EffectItem";
import type { Handleable } from "@core/types/Events";

const props = defineProps<{
  effects: EffectItem[];
  selectedEffect?: { type: "effect"; id: number };
}>();

const emit = defineEmits<{
  (event: "select", effect: EffectItem | null): void;
  (event: "delete", effect: EffectItem): void;
  (event: "copy", effect: EffectItem): void;
}>();

const { t } = useI18n();

const focussedEffect = ref<EffectItem | null>(null);

const contextMenuData = reactive({
  effect: null as null | EffectItem,
  position: {
    x: 0,
    y: 0
  }
});

const focusButtonRefs = ref<Record<number, HTMLElement | null>>({});
type ExpressionInputElement = InstanceType<typeof ExpressionInput>;
const effectExpressionFields = ref<Record<number, ExpressionInputElement | null>>({});

const targets = computed(() => useTargetStore().items);
const resources = computed(() => useResourceStore().items);
const indices = computed(() => useIndexStore().items);

const effectRoundLengths = computed((): number[] => {
  return props.effects.map((effect: EffectItem) => {
    return getEffectRoundLength(effect);
  });
});

const roundUnit = computed(() => {
  return useSettingsStore().simulationRoundUnit;
});

function updateImpulseExpression(
  effect: EffectItem,
  index: number,
  value: string
) {
  const impulseExpressions = effect.impulseExpressions ?? [];
  impulseExpressions[index] = value;
  useEffectStore().update({
    id: effect.id,
    impulseExpressions
  });
}

function getTotalEffectValue(effect: EffectItem) {
  if (Effect.isSpecial(effect)) {
    return t("objectNames.effect");
  }

  const value = effect.impulseExpressions.reduce(
    (total: number, expression: string) => {
      try {
        const calculated = calculateExpression(expression, {
          targets: targets.value,
          resources: resources.value,
          indices: indices.value
        });
        return total + Number(calculated.value);
      } catch (error) {
        return total;
      }
    },
    0
  );

  if (value >= 0) {
    return `+${formatNumber(value)}`;
  }

  return formatNumber(value);
}

/**
 * Calculates the amount of rounds the effect affects.
 */
function getEffectRoundLength(effect: EffectItem) {
  let lastIndex = 0;
  for (let i = 0; i < effect.impulseExpressions.length; i++) {
    const expression = effect.impulseExpressions[i];
    if (expression != null && expression != "") {
      lastIndex = i;
    }
  }
  return lastIndex + 1;
}

function deleteEffect(effect: EffectItem) {
  if (effect.id == props.selectedEffect?.id) {
    emit("select", null);
  }
  emit("delete", effect);
  const effectStore = useEffectStore();
  effectStore.delete({
    id: effect.id
  });
}
function onEffectClick(
  mouseEvent: MouseEvent & Handleable,
  effect: EffectItem | null
) {
  if (mouseEvent.hasBeenHandled || mouseEvent.defaultPrevented) {
    return;
  }
  mouseEvent.preventDefault();
  const focusButton = effect ? focusButtonRefs.value[effect.id] : null;
  if (props.selectedEffect?.id == effect?.id) {
    focusButton?.blur?.();
    emit("select", null);
  } else {
    focusButton?.focus?.();
    emit("select", effect);
  }
}
function onEffectContextMenu(event: PointerEvent, effect: EffectItem) {
  event.preventDefault();
  contextMenuData.effect = effect;
  if (event.pointerType === "") {
    // triggered by keyboard
    if (event.target instanceof HTMLElement) {
      const targetPos = event.target.getBoundingClientRect();
      contextMenuData.position.x = targetPos.x + 5;
      contextMenuData.position.y = targetPos.y + 5;
    }
  } else {
    contextMenuData.position.x = event.clientX;
    contextMenuData.position.y = event.clientY;
  }
}

/**
 * @param effect - the effect to delete
 */
function onContextmenuDelete(effect: EffectItem | null) {
  if (effect == null) return;
  contextMenuData.effect = null;
  deleteEffect(effect);
}

/**
 * @param effect - the effect to select
 */
function onContextmenuOpenRounds(effect: EffectItem | null) {
  if (effect == null) return;
  contextMenuData.effect = null;
  useDrawerStates().open("effectOverRounds");
  emit("select", effect);
}

function onContextMenuOpenVisibility(effect: EffectItem | null) {
  if (effect == null) return;
  contextMenuData.effect = null;
  useDrawerStates().open("effectVisibility");
  emit("select", effect);
}

function onContextmenuDeselect() {
  contextMenuData.effect = null;
  emit("select", null);
}

function toClipboard(effectItem: EffectItem | null) {
  if (effectItem == null) return;
  emit("copy", effectItem);
}

function focusEffectExpressionField(effect: { id: number }) {
  effectExpressionFields.value[effect.id]?.focus();
}

function updateOrder(newOrder: number[]) {
  useEffectStore().saveOrder(newOrder);
}

defineExpose({
  focusEffectExpressionField
});
</script>

<style lang="scss">
.effectlist {
  &__line {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: minmax(2rem, 1fr) min-content;
    gap: 0.5em;
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: 0.5em;
  }

  &__entry {
    border: none;
    background-color: transparent;
    padding: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-text-medium);
    font-weight: 600;
    min-width: 0;

    &:focus-visible {
      outline: none;
    }
  }

  &__input {
    min-width: 8rem;
    width: 100%;
  }

  &__summary {
    font-style: italic;
    font-size: 13px;
    border: none;
    background-color: transparent;
    padding: 0.6em 0;
    color: var(--color-text-medium);

    &:hover {
      color: var(--color-text);
    }
  }
}

.effect-item {
  background-color: rgba(var(--tint-contrast), 0.01);
  border: 1px solid transparent;
  padding: 0.5rem;
  border-radius: 3px;
  box-shadow: 0 1px 5px var(--color-shadow-2);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  outline: 1px solid transparent;
  transition: outline-color 100ms ease;

  &.-focussed {
    outline: 1px dashed var(--color-action);
  }

  &.-selected {
    border-color: var(--color-action);
    background-color: var(--color-background-close);
  }

  &.-focussed.-selected {
    outline: none;
  }

  &:hover {
    background-color: var(--color-background-close);
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  }

  &__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) min-content min-content;
    grid-template-areas: "text docAnchor context";
    gap: 0.25rem;
    min-width: 0;
    max-width: 100%;
    align-items: center;
  }

  &__title {
    border: none;
    background-color: transparent;
    padding: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-text-medium);
    font-weight: 600;
    min-width: 0;

    &:focus-visible {
      outline: none;
    }
  }

  &__titletext {
    display: block;
    overflow: hidden;
    hyphens: auto;
    font-size: inherit;
    text-align: left;
    text-overflow: ellipsis;
  }

  &__docanchor {
    font-size: 1em;
  }
  &__contextbutton {
    padding: 0.25rem;
  }

  &__background {
    background-color: var(--color-background-deep);
  }
}
</style>
